import * as THREE from "three";

// Additive cone trail extending behind the ball along +Z (toward the
// camera). The ball itself barely moves in world space (it's pinned at
// origin and the world scrolls past it), so a true position-history
// trail would just be a static blob. Instead we render a fixed-shape
// cone with a UV-scrolling shader to *suggest* motion, and tint it by
// the current combo tier so the player feels their momentum building.
//
// Cheap: ~36 verts, one mesh, one draw call. Additive blend, no depth
// write, so it composites cleanly with everything else.

const TRAIL_LENGTH = 2.6;        // how far behind the ball it extends (m)
const TRAIL_HEAD_RADIUS = 0.42;  // matches PLAYER_RADIUS-ish
const TRAIL_TAIL_RADIUS = 0.08;
const TRAIL_RADIAL_SEGMENTS = 18;
const COLOR_LERP_LAMBDA = 6;

// Default palette mirrors the original const so callers that never
// touch setTierColors() still get the original look. CosmeticsService
// overwrites this per-instance via setTierColors() at startGame().
const DEFAULT_TIER_COLORS: readonly [number, number, number, number] = [
  0xffffff, // tier 0: pristine
  0x2bd3ff, // tier 1: cyan
  0xff5cb1, // tier 2: pink
  0xffd23f, // tier 3+: gold
];

const VERT = /* glsl */ `
  varying float vU;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    // After the construction-time rotate+translate, geometry-V=1 lands at
    // the ball end and V=0 at the tail. The fragment shader's existing
    // alpha math peaks just past vU=0, so flip the V coordinate here to
    // place that peak at the ball.
    vU = 1.0 - uv.y;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const FRAG = /* glsl */ `
  precision mediump float;
  uniform vec3  uColor;
  uniform float uTime;
  uniform float uIntensity;
  varying float vU;
  varying vec2  vUv;

  void main() {
    // vU = 0 at the head (touching the ball), 1 at the tail.
    // Soft alpha fade head -> tail with a gentle attack on the very
    // first centimeter so the trail doesn't visibly "start" at the ball.
    float head = smoothstep(0.0, 0.05, vU);
    float fade = 1.0 - vU;
    float alpha = head * fade * fade;

    // UV scroll on the longitudinal axis to fake motion.
    float scroll = fract(vU * 3.0 - uTime * 1.6);
    float pulse  = 0.6 + 0.4 * smoothstep(0.0, 0.4, scroll) *
                          (1.0 - smoothstep(0.4, 1.0, scroll));

    // Slight radial brightening at the center axis so the cone reads
    // like a beam rather than a flat ribbon.
    float radial = smoothstep(0.6, 0.0, abs(vUv.x - 0.5) * 2.0);

    vec3 col = uColor * pulse * (0.6 + 0.4 * radial) * uIntensity;
    gl_FragColor = vec4(col, alpha * (0.7 + 0.3 * pulse));
  }
`;

export class PlayerTrail {
  readonly mesh: THREE.Mesh;
  private material: THREE.ShaderMaterial;
  private geometry: THREE.BufferGeometry;
  private targetColor = new THREE.Color(DEFAULT_TIER_COLORS[0]);
  private currentColor = new THREE.Color(DEFAULT_TIER_COLORS[0]);
  // Per-instance tier palette so different cosmetic selections can
  // recolor the trail without forking the shader. Defaults to the
  // original constant so anything that doesn't call setTierColors()
  // looks identical to the pre-cosmetic version.
  private tierColors: number[] = [...DEFAULT_TIER_COLORS];

  constructor() {
    // CylinderGeometry along Y by default; pass radiusTop=HEAD so that
    // the wide ball-side ends up at +Y. After the rotateX(-PI/2) below,
    // +Y maps to -Z, and the subsequent translate(0,0,+L/2) lands the
    // wide end at z = 0 (the ball) and the narrow tail at z = +L
    // (toward the camera at z ≈ +6.5).
    this.geometry = new THREE.CylinderGeometry(
      TRAIL_HEAD_RADIUS,
      TRAIL_TAIL_RADIUS,
      TRAIL_LENGTH,
      TRAIL_RADIAL_SEGMENTS,
      1,
      true
    );
    this.geometry.rotateX(-Math.PI / 2);
    this.geometry.translate(0, 0, TRAIL_LENGTH / 2);

    this.material = new THREE.ShaderMaterial({
      uniforms: {
        uColor: { value: this.currentColor },
        uTime: { value: 0 },
        uIntensity: { value: 1 },
      },
      vertexShader: VERT,
      fragmentShader: FRAG,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
    });

    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 5; // draw after the tunnel walls
  }

  attach(parent: THREE.Object3D): void {
    parent.add(this.mesh);
  }

  detach(): void {
    this.mesh.parent?.remove(this.mesh);
  }

  // Track the player ball position and combo tier each frame.
  // playerMesh is parented to scene.scene (same parent as this.mesh) so
  // we can copy its world-equivalent position directly.
  follow(playerMesh: THREE.Object3D, dt: number, comboTier: number): void {
    this.mesh.position.x = playerMesh.position.x;
    this.mesh.position.y = playerMesh.position.y;
    // Ball is at z=0; trail head sits there and extends to +z (toward camera).
    this.mesh.position.z = playerMesh.position.z;

    const tierIdx = Math.max(
      0,
      Math.min(this.tierColors.length - 1, comboTier)
    );
    this.targetColor.setHex(this.tierColors[tierIdx]!);
    const k = 1 - Math.exp(-COLOR_LERP_LAMBDA * dt);
    this.currentColor.lerp(this.targetColor, k);

    this.material.uniforms["uTime"]!.value += dt;
    // Slightly boost intensity on higher tiers so gold runs read hot.
    this.material.uniforms["uIntensity"]!.value = 0.85 + tierIdx * 0.18;
  }

  setVisible(v: boolean): void {
    this.mesh.visible = v;
  }

  // Replace the per-tier color list. Caller is expected to pass at
  // least one entry; the follow() clamp handles short / long lists
  // gracefully. Cheap; mutates state for the next follow() frame.
  setTierColors(colors: readonly number[]): void {
    if (colors.length === 0) return;
    this.tierColors = [...colors];
  }

  dispose(): void {
    this.detach();
    this.geometry.dispose();
    this.material.dispose();
  }
}
